import { QueryClient } from '@tanstack/react-query';

const MAX_RETRIES = 3;

function getHttpStatus(error: unknown): number | undefined {
	if (!error || typeof error !== 'object') {
		return undefined;
	}

	const candidate = error as {
		status?: number;
		response?: { status?: number };
		message?: string;
	};

	if (typeof candidate.status === 'number') {
		return candidate.status;
	}

	if (typeof candidate.response?.status === 'number') {
		return candidate.response.status;
	}

	if (typeof candidate.message === 'string') {
		const match = candidate.message.match(/\b(4\d\d|5\d\d)\b/);
		if (match) {
			return Number(match[1]);
		}
	}

	return undefined;
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Keep list/detail data fresh enough for chat UX without refetching on every render.
			staleTime: 30 * 1000, // 30 seconds
			// Retain inactive query cache to support quick back/forward navigation.
			gcTime: 10 * 60 * 1000, // 10 minutes
			// No revalidate server state when users return to the tab.
			refetchOnWindowFocus: false,
			retry: (failureCount, error) => {
				if (typeof navigator !== 'undefined' && !navigator.onLine) {
					return false;
				}

				const status = getHttpStatus(error);

				// 4xx errors are usually user/input/auth related, so retrying is not helpful.
				if (status && status >= 400 && status < 500) {
					return false;
				}

				// Retry network/5xx-like failures a small number of times.
				return failureCount < MAX_RETRIES;
			},
			// Exponential backoff to reduce pressure during transient outages.
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000), // Max 30 seconds
		},
		mutations: {
			// Mutations are not retried globally to avoid duplicate side effects.
			retry: 0,
		},
	},
});
